// Login.jsx
"use client";
import { supabase } from "@/lib/supabase";
import { useRouter } from "next/navigation";
import { useEffect, useState } from "react";
import Messages from "./messages";
import { useAuth } from "@/providers/AuthProvider";
import Link from "next/link";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const router = useRouter();
  const { session } = useAuth();

  useEffect(() => {
    if (session) router.push("/");
  }, [session]);

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage("");

    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      setMessage(error.message);
      setLoading(false);
      return;
    }

    const user_id = data.user?.id;
    if (user_id) {
      try {
        const { data: existingUserChatLimit, error: selectError } =
          await supabase
            .from("user_chat_limits")
            .select("id")
            .eq("user_id", user_id)
            .maybeSingle();

        if (selectError) {
          setMessage("Error checking user chat limits");
          setLoading(false);
          return;
        }

        if (!existingUserChatLimit) {
          const { error: insertError } = await supabase
            .from("user_chat_limits")
            .insert([
              {
                user_id,
                email,
                chat_count: 0,
                chat_limit: 10,
              },
            ]);

          if (insertError) {
            setMessage("Error inserting user chat limits");
            setLoading(false);
            return;
          }
        }

        router.push("/");
      } catch (err) {
        setMessage("An unexpected error occurred");
      }
    }

    setLoading(false);
  };

  return (
    <div className="flex-1 flex flex-col w-full px-8 sm:max-w-md justify-center gap-2">
      <form
        onSubmit={handleSignIn}
        className="flex-1 flex flex-col w-full justify-center gap-2 text-foreground"
      >
        <label className="text-md" htmlFor="email">
          Email
        </label>
        <input
          className="rounded-md px-4 py-2 bg-inherit border mb-4"
          type="email"
          name="email"
          placeholder="you@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />

        <label className="text-md" htmlFor="password">
          Password
        </label>
        <div className="relative">
          <input
            className="rounded-md px-4 py-2 bg-inherit border mb-4 w-full"
            type={showPassword ? "text" : "password"}
            name="password"
            placeholder="••••••••"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
          <button
            type="button"
            className="absolute right-2 top-2 text-sm text-gray-500 hover:text-gray-700"
            onClick={() => setShowPassword(!showPassword)}
          >
            {showPassword ? "Hide" : "Show"}
          </button>
        </div>

        <button
          type="submit"
          className="bg-black rounded px-4 py-2 text-white mb-2"
          disabled={loading}
        >
          Sign In
        </button>

        <Link
          href={"/signup"}
          className="border text-center border-gray-700 rounded px-4 py-2 text-black mb-2"
        >
          Sign Up
        </Link>

        <button
          onClick={() => router.push("/auth/forgotpass")}
          className="border border-gray-700 rounded px-4 py-2 text-black mb-2"
        >
          Forgot Password?
        </button>

        {/* Terms and Conditions Link */}
        <Link
          href="/terms"
          className="text-center text-sm text-gray-500 hover:text-gray-700 underline"
        >
          Terms and Conditions
        </Link>

        <Messages error={""} message={message} />
      </form>
    </div>
  );
}